import { useParams, useNavigate } from "react-router-dom";
import {
  AlertInfo,
  Email,
  Link,
  LocationAlt,
  Phone,
  Plus,
  Settings,
  Whatsapp,
} from "assets/icons";
import { Button, Spacer } from "@heroui/react";
import { CompanyType, ContactTags } from "shared/types/contacts.ts";
import { useEffect, useState } from "react";
import { Contact } from "components/contact";
import DocumentsList from "components/documents/documentsList";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import InputSkeleton from "shared/skeletons/input.skeleton";
import TagSelector from "screens/contacts/components/tagSelector";
import { useResources } from "shared/contexts/resources.context";
import { AddressPicker, SectionHeader, renderSection } from "components/index";
import { IncassoDebtSection } from "shared/components/rental/components/IncassoDebtSection";
import { ContactBuildingLinksSection } from "screens/contactDetails/components/ContactBuildingLinksSection";
import FileUploadComponent from "components/documents/fileUpload";
import { ResourceType } from "shared/types/resource";
import { FieldType } from "shared/types/field";
import DirectSearchComponent, {
  SearchMode,
} from "shared/components/searchBar/components/directSearchComponent";

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanyType | null>(null);
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState<boolean>(!!id);
  const [editMode, setEditMode] = useState<boolean>(!id);
  const [editedCompany, setEditedCompany] = useState<CompanyType | null>(!id ? ({} as CompanyType) : null);
  const [selectedTags, setSelectedTags] = useState<string[]>(
    company?.tags || [],
  );
  const { refreshResources } = useResources();
  const [refetchDocuments, setRefetchDocuments] = useState({});
  const [isLinkingContact, setIsLinkingContact] = useState(false);

  /**
   * Koppel een bestaande persoon aan dit bedrijf.
   *
   * De knop "Koppeling toevoegen" was een `<a href="">` zonder handler: klikken
   * herlaadde de pagina en er gebeurde niets. Koppelen betekent hier het bedrijf
   * op het contact zetten — dat is de kant waar de relatie staat
   * (Contact.company -> ContactCompany.contacts).
   */
  const linkContactToCompany = async (contactId: number) => {
    if (!company?.id || !Number.isFinite(contactId)) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/${contactId}/company`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ companyId: company.id }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error(await response.text().catch(() => response.statusText));
      }

      setIsLinkingContact(false);
      await fetchCompany();
      refreshResources();
      toast.success("Contact gekoppeld aan bedrijf");
    } catch (error) {
      console.error("Failed to link contact to company:", error);
      toast.error("Koppelen van het contact is mislukt");
    }
  };

  useEffect(() => {
    // Listener for Escape key to exit edit mode
    const handleKeydown = (e: KeyboardEvent) => {
      if (editMode && e.key === "Escape") {
        toggleEditMode();
      }
    };

    document.addEventListener("keydown", handleKeydown);
    return () => {
      // Cleanup event listener
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [company, editMode]);

  useEffect(() => {
    if (editMode) {
      setSelectedTags(company?.tags || []);
    }
  }, [company, editMode, company?.tags]);

  const handleUploadSuccess = () => {
    // TODO: do something to refetch the component
    setRefetchDocuments({});
  };

  const fetchCompany = async () => {
    if (id) {
      try {
        const data = await fetchWithAuth(
          `${API_URL}/contact/company/${id}`,
          { method: "GET" },
          session,
        );
        if (!data.ok) {
          throw new Error("Network response was not ok");
        }
        const c: any = await data.json();
        console.log(c);
        setCompany(c);
      } catch (error) {
        console.error("Failed to fetch contact: ", error);
        toast("Error bij laden van bedrijf");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isValidCompany = (company: CompanyType) => {
    //  When creating a company these fields should be at least present
    return company?.name;
  };

  const saveCompanyUpdates = async () => {
    if (!editedCompany || !isValidCompany(editedCompany)) {
      toast.error("Een bedrijf moet minstens een naam hebben.");
      return;
    }

    const updatedCompany = {
      ...editedCompany,
      tags: selectedTags,
    };

    try {
      let newCompanyData;
      if (id) {
        // Updating an existing contact

        await fetchWithAuth(
          `${API_URL}/contact/update-company/${company?.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedCompany),
          },
          session,
        );
        fetchCompany();
        toast("Bedrijf bewaard!");
      } else {
        // Creating a new company
        const response = await fetchWithAuth(
          `${API_URL}/contact/create-company`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatedCompany),
          },
          session,
        );

        newCompanyData = await response.json();
        if (newCompanyData && newCompanyData.id) {
          setCompany(newCompanyData);
          navigate(`/company/${newCompanyData.id}`);
        }
      }

      refreshResources();
    } catch (error) {
      console.error("Failed to save contact: ", error);
      toast("Error saving contact!");
    } finally {
      setEditMode(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchCompany();
    }
  }, [id]);

  const toggleEditMode = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedCompany(company); // initialize editedContact when entering edit mode
    }
  };

  const handleTagClick = (tag: string) => {
    if (selectedTags.includes(tag)) {
      // Remove tag
      setSelectedTags((prevTags) => prevTags.filter((t) => t !== tag));
    } else {
      // Add tag
      setSelectedTags((prevTags) => [...prevTags, tag]);
    }
  };

  const getMapsURL = (address: string): string => {
    const encodedAddress = encodeURIComponent(address);

    // Detecting iOS and Android devices
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS =
      /iPad|iPhone|iPod/.test(userAgent) && !(navigator as any).standalone;
    const isAndroid = /android/i.test(userAgent);

    if (isIOS) {
      return `https://maps.apple.com/?address=${encodedAddress}`;
    } else if (isAndroid) {
      return `geo:0,0?q=${encodedAddress}`;
    } else {
      return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }
  };

  const handleAddressSelect = (selectedPlace: any) => {
    console.log("Selected address:", selectedPlace);
    setEditedCompany((prev) => ({
      ...prev,
      address: {
        ...prev?.address,
        placeId: selectedPlace.placeId,
        formattedAddress: selectedPlace.description,
      },
    }));

    setCompany((prev) => ({
      ...prev,
      address: {
        ...prev?.address,
        placeId: selectedPlace.placeId,
        formattedAddress: selectedPlace.description,
      },
    }));
  };

  const sharedCompanyProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: toggleEditMode,
    onSaveClick: saveCompanyUpdates,
    editedState: editedCompany,
    originalState: company,
    setStateFunction: setEditedCompany,
  };

  const companyFieldsConfig = [
    { label: "Bedrijfsnaam", key: "name" },
    {
      label: "Adres",
      Component: () => (
        <div className={editMode ? "grow p-2" : ""}>
          <AddressPicker
            editMode={editMode}
            defaultAddress={
              editedCompany?.address?.formattedAddress ||
              company?.address?.formattedAddress ||
              ""
            }
            getAddress={(selectedPlace) => handleAddressSelect(selectedPlace)}
          />
        </div>
      ),
    },
    { label: "Mobiel", key: "phone_direct", type: FieldType.PHONE },
    { label: "E-mail", key: "email", type: FieldType.EMAIL },
    { label: "BTW-nummer", key: "vatNumber" },
    { label: "Website", key: "website" },
    { label: "Beschrijving", key: "description" },
  ];

  const companyFieldsExtraConfig = [
    { label: "Odoo ID", key: "odoo_id", disableEditMode: true },
    { label: "Staatsbladmonitor", key: "staatsbladmonitor_url" },
    { label: "KBO", key: "kbo_url" },
    { label: "LinkedIn", key: "linkedIn" },
    { label: "Facebook", key: "facebook" },
    { label: "Instagram", key: "instagram" },
    {
      label: "Tags",
      key: "tags",
      Component: () => {
        if (editMode) {
          return (
            <div className="flex flex-col gap-2">
              <p className="text-sm">Tags</p>
              <TagSelector
                tags={ContactTags}
                selectedTags={selectedTags}
                handleTagClick={handleTagClick}
              />
            </div>
          );
        } else if (company?.tags && company.tags.length > 0) {
          return (
            <div className="flex flex-wrap gap-2">
              {company.tags.sort().map((tag, index) => (
                <span
                  key={index}
                  className="h-fit min-w-fit rounded-full border border-moss-primary bg-white px-2 font-medium text-moss-primary"
                >
                  {tag}
                </span>
              ))}
            </div>
          );
        } else {
          return <p className="font-medium">Niet ingevuld</p>;
        }
      },
    },
  ];

  return (
    <div className="z-10 mb-10">
      {!isLoading ? (
        <div className="flex flex-col items-center gap-2.5 sm:grow sm:flex-row">
          <div className="flex flex-col items-center gap-0 sm:grow sm:items-start">
            <p className="text-3xl font-medium">{company?.name}</p>
          </div>
          <ul className="flex flex-row items-center justify-center gap-2.5">
            {company?.phone_direct && (
              <li>
                <a
                  href={"tel:" + company?.phone_direct}
                  className="flex items-center justify-center rounded-full bg-white p-2 text-moss-primary shadow-md"
                >
                  <Phone />
                </a>
              </li>
            )}
            {company?.phone_direct && (
              <li>
                <a
                  href={"whatsapp://" + company?.phone_direct}
                  className="flex items-center justify-center rounded-full bg-white p-2 text-moss-primary shadow-md"
                >
                  <Whatsapp />
                </a>
              </li>
            )}
            {company?.email && (
              <li>
                <a
                  href={"mailto:" + company?.email}
                  className="flex items-center justify-center rounded-full bg-white p-2 text-moss-primary shadow-md"
                >
                  <Email />
                </a>
              </li>
            )}
            {company?.address?.formattedAddress && (
              <li>
                <a
                  href={getMapsURL(company.address.formattedAddress)}
                  className="flex items-center justify-center rounded-full bg-white p-2 text-moss-primary shadow-md"
                  target="_blank" // Opens in a new tab
                  rel="noopener noreferrer" // For security reasons
                >
                  <LocationAlt />
                </a>
              </li>
            )}
            {/*
            <li>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault(); // Prevent default anchor tag behavior
                  toggleSubMenu();
                }}
                className="flex items-center justify-center rounded-full bg-transparent p-2 text-moss-primary shadow-[inset_0_0_0_1px_rgba(49,64,59,0.25)]"
              >
                <More />
              </a>
            </li>
            */}
          </ul>
          {/*
          {isSubMenuVisible && (
            <div ref={menuRef}>
              <SubMenu triggerRef={triggerRef}>
                <SubMenuCell
                  onClick={() => console.log("Hello world")}
                  key="Open meeting notes"
                  label="Open meeting notes"
                  icon={<Note />}
                  className="flex cursor-pointer items-center gap-2 bg-status-medium"
                />
                <SubMenuCell
                  onClick={() => console.log("Hello world")}
                  key="Verwijder contact"
                  label="Verwijder contact"
                  icon={<Trash />}
                  className="flex cursor-pointer items-center gap-2 bg-status-medium text-status-bad"
                />
              </SubMenu>
            </div>
          )}
          */}
        </div>
      ) : (
        <InputSkeleton />
      )}
      <Spacer y={10} />
      {/* Content */}
      <div className="sm:rounded-lg sm:bg-white sm:p-5 sm:shadow-md">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            {renderSection({
              title: "Gegevens",
              icon: <AlertInfo />,
              fieldsConfig: companyFieldsConfig,
              ...sharedCompanyProps,
            })}
          </div>
          <div>
            {renderSection({
              title: "Uitgebreide gegevens",
              icon: <Settings />,
              fieldsConfig: companyFieldsExtraConfig,
              ...sharedCompanyProps,
            })}
          </div>
          {/* Gebouw Koppelingen Section */}
          {company?.id && (
            <div className="sm:col-span-2">
              <ContactBuildingLinksSection companyId={company.id} />
            </div>
          )}

          {/* Incasso Debt Section */}
          {company?.id && (
            <div className="sm:col-span-2">
              <IncassoDebtSection companyId={company.id} />
            </div>
          )}

          <div className="sm:col-span-2">
            <div className="flex flex-col gap-2 rounded-md border border-neutral-primary/15 bg-white pb-3">
              <DocumentsList
                refetchDocuments={refetchDocuments}
                sharePointFolderId={company?.sharePointFolderId as string}
                sharePointFolderUrl={company?.sharePointFolderUrl as string}
              />
              <FileUploadComponent
                resourceType={ResourceType.COMPANY}
                resource={company}
                onUploadSuccess={handleUploadSuccess}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 rounded-md border border-neutral-primary/15 bg-white pb-3 sm:col-span-2">
            <SectionHeader
              title="Gekoppelde contacten"
              type="action"
              icon={<Link />}
              action={
                <Button
                  startContent={<Plus className="h-[18px] w-[18px]" />}
                  onClick={() => setIsLinkingContact((open) => !open)}
                  className="h-fit min-w-fit gap-1 rounded-none bg-transparent p-0 text-sm font-medium text-moss-primary hover:bg-transparent"
                >
                  {isLinkingContact ? "Annuleren" : "Koppeling toevoegen"}
                </Button>
              }
            />
            {isLinkingContact && (
              <div className="px-2.5 pb-1">
                <DirectSearchComponent
                  label="Zoek een persoon om te koppelen"
                  resourceFilter={[ResourceType.CONTACT]}
                  mode={SearchMode.SEARCH_AND_PRESENT}
                  editMode
                  onSelect={(resource) =>
                    linkContactToCompany(Number(resource?.resourceId))
                  }
                />
              </div>
            )}
            <div className="flex flex-col gap-2.5 px-2.5 sm:flex-row sm:flex-wrap sm:gap-4">
              {company?.contacts?.length ? (
                company.contacts.map((contact) => (
                  <Contact key={contact.id} contact={contact} />
                ))
              ) : (
                <p className="text-neutral-secondary-mid px-1 text-sm">
                  Nog geen contacten gekoppeld
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
